import React, { useMemo } from 'react'
import {
  getInventoryMessage,
  getSkuInventoryStatus,
  i18nTranslate,
  enablePlpRedesign,
  enablePdpRedesign,
} from 'src/utils'
import { UNIX_TIMESTAMP_SECONDS_LENGTH } from 'src/utils/constant'
import { dateFormatDeps } from 'src/deps'
import uniqBy from 'lodash/uniqBy'
import './ProductStatusBadge.scss'

const normalizeToMilliseconds = dateValue => {
  if (typeof dateValue === 'string') {
    const dateOnlyValue = dateValue.split('T')[0]
    return new Date(dateOnlyValue).getTime()
  }
  return Number(
    dateValue?.toString?.().length <= UNIX_TIMESTAMP_SECONDS_LENGTH
      ? dateValue + '000'
      : dateValue
  )
}

const getBackOrderDateParts = dateValue => {
  if (typeof dateValue !== 'string' || !dateValue.includes('T')) {
    return null
  }

  const [year, month, date] = dateValue.split('T')[0].split('-').map(Number)
  return date && month && year ? { date, month, year } : null
}

const formatDateMessage = (
  dateValue,
  translationKey,
  displayMessage,
  fallbackDisplayMessage = null
) => {
  if (!dateValue) return fallbackDisplayMessage

  try {
    const timestamp = normalizeToMilliseconds(dateValue)

    if (timestamp && timestamp > Date.now()) {
      const { date, month, year } = dateFormatDeps.convertTimeStampToDate(
        timestamp,
        false
      )
      const formatedDateAsPerLocale =
        dateFormatDeps.getFormattedDateAsPerLocale(date, month, year)

      return `${i18nTranslate(translationKey, displayMessage)} ${formatedDateAsPerLocale}`
    }

    return fallbackDisplayMessage
  } catch (error) {
    console.error('Error formatting date message:', error)
    return fallbackDisplayMessage
  }
}

const formatBackOrderedMessage = expectedDate => {
  if (!expectedDate)
    return i18nTranslate('backOrdered', 'Back Ordered', {
      nameSpace: 'ssr-resource',
    })

  try {
    const timestamp = normalizeToMilliseconds(expectedDate)

    if (timestamp && timestamp > Date.now()) {
      const { date, month, year } =
        getBackOrderDateParts(expectedDate) ||
        dateFormatDeps.convertTimeStampToDate(timestamp, false)

      if (enablePdpRedesign()) {
        const shortMonthName = dateFormatDeps
          .matchMonthFromNumberToString(month)
          ?.slice(0, 3)
        const shortDate = `${shortMonthName} ${date}`
        return `${i18nTranslate(
          'back_ordered_new_label',
          'Back ordered - Ships',
          {
            nameSpace: 'ssr-resource',
          }
        )} ${shortDate}`
      } else {
        const formatedDateAsPerLocale =
          dateFormatDeps.getFormattedDateAsPerLocale(date, month, year)
        return `${i18nTranslate('backordered', 'Ships by', {
          nameSpace: 'ssr-resource',
        })} ${formatedDateAsPerLocale}`
      }
    }

    return i18nTranslate('backOrdered', 'Back Ordered', {
      nameSpace: 'ssr-resource',
    })
  } catch (error) {
    console.error('Error formatting back order message:', error)
    return i18nTranslate('backOrdered', 'Back Ordered', {
      nameSpace: 'ssr-resource',
    })
  }
}

const formatAvailableMessage = availabilityDate => {
  return formatDateMessage(
    availabilityDate,
    'availableBy',
    'Available by',
    null
  )
}

/**
 * Returns the discontinued message shared by PLP and PDP,
 * so both pages display the same product status message
 */
const getDiscontinuedMessage = () =>
  enablePdpRedesign()
    ? i18nTranslate(
        'redesignbundlediscontinuedv2message',
        'This product has been discontinued',
        {
          nameSpace: 'ssr-resource',
        }
      )
    : i18nTranslate(
        'bundle.discontinuedV2Message',
        'Discontinued - This product or promotion has been discontinued',
        {
          nameSpace: 'ssr-resource',
        }
      )

const getProductStatuses = (product = {}, selectedPlpSku) => {
  const isBundleOrKit = product?.type === 'kit' || product?.type === 'bundle'
  const sku = enablePlpRedesign()
    ? isBundleOrKit
      ? product
      : selectedPlpSku?.[0] || product?.sku?.[0] || product
    : product?.sku?.[0] || product

  const statuses = {}
  const status = sku?.properties?.status ?? sku?.properties?.productStatus ?? ''
  const availabilityDate =
    sku?.properties?.availabilityDate ||
    sku?.skuProperties?.availabilityDate ||
    null
  const inventoryData = getInventoryMessage({
    otherProperties: {
      productStatus: sku?.properties?.productStatus || '',
      properties: {
        customerTypes: product?.customerTypes || '',
        dangerousGoods:
          sku?.properties?.dangerousGoods ||
          product?.properties?.dangerousGoods ||
          '',
      },
      dangerousGoods:
        sku?.properties?.dangerousGoods ||
        product?.properties?.dangerousGoods ||
        '',
    },
  })

  if (inventoryData) {
    const isDiscontinued =
      sku?.properties?.productStatus?.toLowerCase() === 'discontinued'

    if (typeof inventoryData === 'object' && inventoryData.message) {
      statuses.inventory = {
        ...inventoryData,
        message: isDiscontinued
          ? getDiscontinuedMessage()
          : inventoryData.message,
      }
    } else {
      const productStatus = sku?.properties?.productStatus
      statuses.inventory = {
        message: isDiscontinued ? getDiscontinuedMessage() : inventoryData,
        key: productStatus || 'inventory',
      }
    }
  }

  if (!statuses.inventory) {
    const inventory = isBundleOrKit
      ? sku?.properties?.inventoryStatus?.toLowerCase()
      : sku?.inventory?.toLowerCase()
    const productStatus = sku?.properties?.productStatus

    if (sku?.inventoryProperties?.backOrdered) {
      const backOrderDate =
        sku?.inventoryProperties?.backOrderISODate ||
        sku?.inventoryProperties?.expectedBackOrderAvailabilityDate

      statuses.backorder = {
        message: formatBackOrderedMessage(backOrderDate),
        key: 'backordered',
      }
    } else if (inventory === 'out of stock') {
      statuses.inventory = {
        message: i18nTranslate('cart.outOfStock', 'Out of stock', {
          nameSpace: 'ssr-resource',
        }),
        key: 'outOfStock',
      }
    } else if (inventory === 'unavailable') {
      statuses.inactiveproduct = {
        message: i18nTranslate('coming.Soon', 'Coming soon..', {
          nameSpace: 'ssr-resource',
        }),
        key: 'comingSoon',
      }
    } else if (productStatus === 'test') {
      statuses.testproduct = {
        message: i18nTranslate('bundle.testSku', 'This is a test sku', {
          nameSpace: 'ssr-resource',
        }),
        key: 'testSku',
      }
    }
  }
  if (status === 'inactive' && availabilityDate) {
    statuses.inactiveproduct = {
      message: formatAvailableMessage(
        sku?.properties?.availabilityDate ||
          sku?.skuProperties?.availabilityDate
      ),
      key: 'availableBy',
    }
    return statuses
  }
  const promoMessagesWithNotZeroDiscount = (product?.promotion || []).filter(
    promo => promo.discount !== 0
  )
  const promoMessages = (promoMessagesWithNotZeroDiscount || [])
    .map(promo => promo?.message?.trim())
    .filter(Boolean)
  const skuPromotionWithNotZeroDiscount = (sku?.promotion || []).filter(
    promo => promo.discount !== 0
  )
  const skuPromotionMessage =
    skuPromotionWithNotZeroDiscount?.[0]?.message?.trim()
  if (promoMessages.length) {
    statuses.promotion = {
      message: uniqBy(promoMessages, promo => promo),
      key: 'promotion',
    }
  } else if (skuPromotionMessage) {
    statuses.promotion = {
      message: skuPromotionMessage,
      key: 'promotion',
    }
  }

  return statuses
}

const getStatusColor = statusType => {
  const colorMap = {
    inventory: 'inventory-status',
    product: 'product-status',
    promotion: 'promotion-status',
    inactiveproduct: 'inactive-product-status',
    testproduct: 'test-product-status',
    backorder: 'backorder-status',
  }
  return colorMap[statusType] || 'default'
}

const ProductStatusBadge = ({
  product,
  variant = 'filled',
  size = 'small',
  selectedPlpSku,
  className = '',
  ...props
}) => {
  const productStatuses = useMemo(
    () => getProductStatuses(product, selectedPlpSku),
    [product, selectedPlpSku]
  )
  const statusEntries = Object.entries(productStatuses)
  const productStatusKey = productStatuses.inventory?.key?.toLowerCase()
  const isOutOfStockOrDiscontinued =
    productStatusKey === 'outofstock' || productStatusKey === 'discontinued'

  const getClassNames = statusType => {
    const baseClass = 'product-status-badge'
    const sizeClass = `${baseClass}--${size}`
    const badgeColor = getStatusColor(statusType)
    const colorClass = `${baseClass}--${badgeColor}-${variant}`

    return `${baseClass} ${sizeClass} ${colorClass} ${className}`.trim()
  }

  // Only render if there are statuses to show
  if (statusEntries.length === 0) {
    return null
  }

  return (
    <div className="product-status-badge-container">
      {statusEntries.map(([statusType, statusData]) => {
        if (!statusData || !statusData?.message) return null

        const messages = Array.isArray(statusData.message)
          ? statusData.message
          : [statusData.message]
        return messages.filter(Boolean).map((message, idx) => (
          <span
            key={`${statusType}-${idx}`}
            id={
              enablePlpRedesign()
                ? selectedPlpSku?.[0]?.properties?.productStatus
                : product?.sku?.[0]?.properties?.productStatus
            }
            data-testid={statusData?.key}
            className={getClassNames(statusType)}
            role="status"
            tabIndex={0}
            aria-label={`${message}`}
            {...props}>
            {message}
          </span>
        ))
      })}
    </div>
  )
}

export {
  ProductStatusBadge,
  getProductStatuses,
  formatDateMessage,
  getBackOrderDateParts,
  getDiscontinuedMessage,
}
export default ProductStatusBadge
